import { cn } from "@newxsystems/shared";
import type { AnchorHTMLAttributes } from "react";
import { getComponent } from "../../../../client";
import { BlockImage } from "../../../shared/components/BlockImage/BlockImage";
import { BlockRichText } from "../../../shared/components/BlockRichText/BlockRichText";
import type { IImageContentGridCardProps } from "./ImageContentGridCard.props";

const cardClasses = "flex h-full min-w-0 flex-col gap-6 border-t border-border py-6 text-start";

export const ImageContentGridCard = ({
  heading,
  description,
  image,
  effectiveAspectRatio,
  imagePosition,
  cardLink,
}: IImageContentGridCardProps) => {
  const Link = getComponent<AnchorHTMLAttributes<HTMLAnchorElement>>("Link");
  const className = cn(
    cardClasses,
    cardLink &&
      "transition-colors duration-200 hover:border-paprika focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-ring",
  );
  const imageContent = (
    <BlockImage
      className="!max-w-none w-full rounded-[14px]"
      effectiveAspectRatio={effectiveAspectRatio}
      image={image}
    />
  );
  const textContent = (
    <div className="flex min-w-0 flex-col gap-4">
      <h3 className="text-[24px]/[1.15] font-semibold tracking-[-0.025em] [overflow-wrap:anywhere]">
        {heading}
      </h3>
      {description ? (
        <BlockRichText
          className="text-base/[1.55] text-onSurface-500 text-pretty [&_a]:underline [&_ol]:ml-5 [&_ol]:list-decimal [&_p+p]:mt-4 [&_ul]:ml-5 [&_ul]:list-disc"
          content={description}
        />
      ) : null}
    </div>
  );
  const content =
    imagePosition === "top" ? (
      <>
        {imageContent}
        {textContent}
      </>
    ) : (
      <>
        {textContent}
        {imageContent}
      </>
    );

  if (cardLink) {
    if (!Link) {
      return null;
    }

    const opensNewTab = cardLink.openInNewTab === true;

    return (
      <Link
        aria-label={cardLink.accessibilityLabel ?? cardLink.label}
        className={className}
        href={cardLink.href}
        rel={opensNewTab ? "noopener noreferrer" : undefined}
        target={opensNewTab ? "_blank" : undefined}
      >
        {content}
      </Link>
    );
  }

  return <div className={className}>{content}</div>;
};

ImageContentGridCard.displayName = "ImageContentGrid::ImageContentGridCard";
